import axios from "axios";

const api = axios.create({
	// Relative "/api" is the correct default: in prod, client and server are
	// the same Cloud Run origin (see apps/docs-console/Dockerfile), so same-
	// origin requests just work. Local dev overrides via VITE_API_URL since
	// client (5180) and server (8080) run as separate origins there.
	baseURL: import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : "/api",
	withCredentials: true,
	timeout: 10000,
	headers: {
		"Content-Type": "application/json",
	},
});

// The default 10s timeout above is fine for plain CRUD, but too short for
// anything that triggers an LLM generation call server-side (Sprint
// Changelog's Haiku call fit under it, but Feature Overview's Sonnet +
// thinking call alone took ~17s in testing) or live Playwright capture
// (launching a real browser + navigating + screenshotting can easily take
// a minute). Pass `{ timeout: GENERATION_TIMEOUT_MS }` as the axios config
// on any call that triggers one of these server-side, rather than raising
// the global default (which would also mask real connectivity issues on
// ordinary fast calls).
export const GENERATION_TIMEOUT_MS = 120000;

// Live Playwright capture (launch browser, log in, navigate + screenshot
// each step of a generated script) is the single slowest operation in this
// app — longer than a text generation call, so it gets its own, more
// generous ceiling rather than sharing GENERATION_TIMEOUT_MS.
export const CAPTURE_TIMEOUT_MS = 180000;

// Codebase scan (docs-generation-service's exploreAndCatalog) is a
// synchronous relay around an agentic loop of up to 40 Claude turns, each
// with its own tool-call round trip — comfortably capable of outlasting
// even CAPTURE_TIMEOUT_MS on a large repo.
export const SCAN_TIMEOUT_MS = 300000;

// Bulk drift check hits Confluence live for every published artifact (bounded
// concurrency, not one request) — scales with library size, not with any
// single artifact's complexity, so it needs its own ceiling rather than the
// global default as the number of published artifacts keeps growing.
export const VERIFY_ALL_TIMEOUT_MS = 60000;

// Request interceptor
api.interceptors.request.use(
	(config) => {
		// Add any auth headers if needed
		return config;
	},
	(error) => {
		return Promise.reject(error);
	}
);

// Response interceptor
api.interceptors.response.use(
	(response) => {
		return response;
	},
	(error) => {
		if (error.response?.status === 401) {
			// Handle unauthorized access
			console.log("Unauthorized access - redirecting to login");
			window.location.href = "/login";
		}

		return Promise.reject(error);
	}
);

export default api;
